import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  MapIcon,
  ArrowPathIcon,
  SparklesIcon,
  ChevronDownIcon,
  InformationCircleIcon,
  Cog6ToothIcon,
  ArrowTopRightOnSquareIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../components/TanTable';
import {
  useAdditionalFieldSelection,
  useAdditionalFieldColumns,
} from '../../hooks/useAdditionalFields';
import { getApiFieldName } from '../../utils/dynamicColumns';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import { OrderDispatcherFilter, DriverDispatcherFilter } from '../../components/filters';
import { DriverSelectorCell } from '../../components/DriverSelectorCell';
import { needsDropReleaseWarning, DROP_RELEASE_WARNING_MESSAGE } from '../../utils/dropReleaseCheck';
import { needsNotReceivingWarning, NOT_RECEIVING_WARNING_MESSAGE } from '../../utils/notReceivingCheck';
import { needsNotRailbilledWarning, NOT_RAILBILLED_WARNING_MESSAGE } from '../../utils/notRailbilledCheck';
import { needsMissingPreadviseWarning, MISSING_PREADVISE_WARNING_MESSAGE } from '../../utils/missingPreadviseCheck';
import { openTroubleTicketWarning, blockingTroubleTicketMessage, legAtOrBeforeRejectedGroup } from '../../utils/openTroubleTicketCheck';
import { redeliveryExpirationWarning } from '../../utils/redeliveryExpirationCheck';
import { openRepairOrderWarning } from '../../utils/openRepairOrderCheck';
import { Drawer } from '../../components/ui/Drawer';
import { Tooltip } from '../../components/ui/Tooltip';
import { RouteMapModal, RouteStop } from '../../components/RouteMapModal';
import GridLabel from '../../components/GridLabel';
import FilterBar from '../../components/FilterBar';
import { useDispatchFooter } from '../../contexts/DispatchFooterContext';
import { useDisplayPreferences } from '../../contexts/DisplayPreferencesContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { useGlobalFilters } from '../../contexts/GlobalFiltersContext';
import userSettingsService from '../../services/userSettings.service';
import { useToast } from '../../contexts/ToastContext';
import ordersService, { Order } from '../../services/orders.service';
import { ViewedProsProvider, useViewedPros } from '../../contexts/ViewedProsContext';
import api from '../../services/api';  // Used for filter options
import { config } from '../../config/config';
import { ProDetailsView } from '../../components/ProDetailsView';
import { useDriverDrawer } from '../../hooks/useDriverDrawer';
import { useVisibilityAwareInterval } from '../../hooks/useVisibilityAwareInterval';
import { EnrichedDriver } from '../../components/DriverDrawer';
import SmartDispatchConfigModal from '../../components/dispatching/SmartDispatchConfigModal';
import styles from './SmartDispatch.module.css';

// Smart Dispatch Suggestion interface
interface SmartDispatchSuggestion {
  id: number;
  pronumber: number;
  legnum: number;
  suggested_driver: string;
  suggestion_rank: number;
  suggestion_score: number;
  score_distance: number;
  score_workload: number;
  score_profitability: number;
  score_qualification: number;
  distance_to_origin_miles: number;
  driver_remaining_dispatches: number;
  driver_current_pay: number;
  driver_daily_goal: number;
  leg_estimated_pay: number;
  suggestion_status: string;
  driver_dispatcher: string | null;
}

// SmartDispatchRow - extends Order with sub-rows for connected moves
interface SmartDispatchRow extends Order {
  // Unique ID for TanTable row identification (must be 'id' for TanTable's getRowId)
  id: string;
  // For sub-rows (legs): specific leg data
  isSubRow?: boolean;
  legOriginName?: string;
  legOriginCityState?: string;
  legDestName?: string;
  legDestCityState?: string;
  legMiles?: number;
  legDrop?: string;
  // Sub-rows for connected moves (parent rows only)
  subLegs?: SmartDispatchRow[];
}

// Background refresh interval in milliseconds (30 seconds)
const BACKGROUND_REFRESH_INTERVAL = 30000;

// ============================================================================
// Utility Functions (defined outside component to maintain stable references)
// ============================================================================

// Get total miles from dispatch scope legs (all legs that will be assigned together)
const getOrderMiles = (order: Order): number | null => {
  if (order.dispatchScopeLegs && order.dispatchScopeLegs.length > 0) {
    const totalMiles = order.dispatchScopeLegs.reduce((sum, leg) => {
      const legMiles = typeof leg.legMiles === 'string' ? parseFloat(leg.legMiles) : (leg.legMiles || 0);
      return sum + legMiles;
    }, 0);
    return totalMiles > 0 ? totalMiles : null;
  }
  return null;
};

// Estimate trip time from miles (assuming average 50 mph)
const getEstimatedTripTime = (miles: number | null): string => {
  if (!miles) return '-';
  const hours = miles / 50;
  if (hours < 1) {
    return `${Math.round(hours * 60)}m`;
  }
  const wholeHours = Math.floor(hours);
  const minutes = Math.round((hours - wholeHours) * 60);
  return minutes > 0 ? `${wholeHours}h ${minutes}m` : `${wholeHours}h`;
};

// Format date for display
const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  } catch {
    return dateStr;
  }
};

// Format score as percentage
const formatScore = (score: number): string => {
  return `${Math.round(score * 100)}%`;
};

// Get score color based on value
const getScoreColor = (score: number): string => {
  if (score >= 0.7) return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30';
  if (score >= 0.5) return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30';
  return 'text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/30';
};

// ============================================================================
// Main Component
// ============================================================================

/** Stable identity on purpose — see useAdditionalFieldColumns. */
const isSmartDispatchSubRow = (row: SmartDispatchRow) => !!row.isSubRow;

function SmartDispatchContent() {
  // Any orders-scope registry field can be added as a column. ONE selection across
  // every region board: a user wanting Bill To on Houston wants it on Dallas too,
  // and the layout follows suit — the per-region settingsKey is gone.
  const fieldSelection = useAdditionalFieldSelection('smart-dispatch');
  const additionalFieldsKey = fieldSelection.additionalFields.join(',');
  const fieldsReady = fieldSelection.ready;
  const { isViewed, markViewed } = useViewedPros();
  // Footer context for "Confirm All" button
  const { setDispatchButton } = useDispatchFooter();
  const { showToast } = useToast();
  const { showGridHighlight, showGridLabels, showGridBorders } = useDisplayPreferences();
  const { permissions, hasAccess } = usePermissions();
  const canEditConfig = hasAccess('/administration/smart-dispatch');

  // Driver drawer hook for nearby drivers on map
  const driverDrawer = useDriverDrawer();

  // Global filters (shared across pages)
  const {
    selectedRegions,
    setSelectedRegions,
    selectedOrderDispatchers,
    setSelectedOrderDispatchers,
    selectedDriverDispatchers,
    setSelectedDriverDispatchers,
    isLoading: globalFiltersLoading,
  } = useGlobalFilters();

  // Local filters (page-specific, but still persisted)
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const localFiltersLoaded = useRef(false);

  // Load local filters from user settings
  useEffect(() => {
    if (localFiltersLoaded.current) return;
    const loadLocalFilters = async () => {
      try {
        const setting = await userSettingsService.getUserSetting('smart_dispatch_filters');
        if (setting?.setting_value) {
          const filters = setting.setting_value;
          if (filters.selectedCustomers) setSelectedCustomers(filters.selectedCustomers);
        }
      } catch (error) {
        // Silently fail
      }
      localFiltersLoaded.current = true;
    };
    loadLocalFilters();
  }, []);

  // Save local filters to user settings
  const saveLocalFiltersTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const saveLocalFilters = useCallback((customers: string[]) => {
    if (saveLocalFiltersTimeoutRef.current) clearTimeout(saveLocalFiltersTimeoutRef.current);
    saveLocalFiltersTimeoutRef.current = setTimeout(async () => {
      try {
        await userSettingsService.saveUserSetting('smart_dispatch_filters', { selectedCustomers: customers });
      } catch (error) {
        console.error('Failed to save local filters:', error);
      }
    }, 500);
  }, []);

  const handleCustomersChange = useCallback((customers: string[]) => {
    setSelectedCustomers(customers);
    saveLocalFilters(customers);
  }, [saveLocalFilters]);

  // Filter options
  const [regionOptions, setRegionOptions] = useState<MultiSelectOption[]>([]);
  const [customerOptions, setCustomerOptions] = useState<MultiSelectOption[]>([]);
  const homeRegionInitialized = useRef(false);

  // Auto-select user's home region when available (only if no global region is set)
  useEffect(() => {
    if (
      !homeRegionInitialized.current &&
      !globalFiltersLoading &&
      selectedRegions.length === 0 &&
      permissions?.home_region &&
      permissions?.home_region_as_default !== false &&
      regionOptions.length > 0 &&
      regionOptions.some(opt => opt.value === permissions.home_region)
    ) {
      setSelectedRegions([permissions.home_region]);
      homeRegionInitialized.current = true;
    }
  }, [permissions?.home_region, permissions?.home_region_as_default, regionOptions, selectedRegions.length, globalFiltersLoading, setSelectedRegions]);

  // Data state
  const [orders, setOrders] = useState<Order[]>([]);
  const [suggestions, setSuggestions] = useState<SmartDispatchSuggestion[]>([]);
  const [loading, setLoading] = useState(false);

  // Map suggestions by pronumber-legnum for quick lookup
  const suggestionsByOrder = useMemo(() => {
    const map = new Map<string, SmartDispatchSuggestion[]>();
    suggestions.forEach(s => {
      const key = `${s.pronumber}-${s.legnum}`;
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key)!.push(s);
    });
    // Sort each group by rank
    map.forEach((value) => {
      value.sort((a, b) => a.suggestion_rank - b.suggestion_rank);
    });
    return map;
  }, [suggestions]);

  // Driver assignments (local state before confirmation)
  const [driverAssignments, setDriverAssignments] = useState<{ [key: string]: string }>({});
  // Ref to access latest driverAssignments without triggering useMemo re-computation
  // This prevents infinite loops when driver assignment changes recreate columns
  const driverAssignmentsRef = useRef(driverAssignments);
  driverAssignmentsRef.current = driverAssignments;
  const [driverQualifications, setDriverQualifications] = useState<{ [key: string]: boolean | null }>({});
  // Ref to access latest driverQualifications without triggering useMemo re-computation
  // This prevents infinite loops when validation status changes update the columns
  const driverQualificationsRef = useRef(driverQualifications);
  driverQualificationsRef.current = driverQualifications;
  const [confirmedMoves, setConfirmedMoves] = useState<Set<string>>(new Set());
  const [pendingConfirms, setPendingConfirms] = useState<Set<string>>(new Set());

  // Drawer state for route map
  const [selectedOrderForMap, setSelectedOrderForMap] = useState<Order | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Alternative suggestions state
  const [alternativesOrder, setAlternativesOrder] = useState<Order | null>(null);
  const [alternativeSuggestions, setAlternativeSuggestions] = useState<any[]>([]);
  const [disqualifiedDrivers, setDisqualifiedDrivers] = useState<any[]>([]);
  const [totalDriversChecked, setTotalDriversChecked] = useState<number>(0);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [isAlternativesDrawerOpen, setIsAlternativesDrawerOpen] = useState(false);
  const [loadingAlternatives, setLoadingAlternatives] = useState(false);
  const [showDisqualified, setShowDisqualified] = useState(false);
  const [showDebug, setShowDebug] = useState(false);

  // Order details modal state
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);

  // Config modal state
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [configRegion, setConfigRegion] = useState<string>('');
  const [currentConfig, setCurrentConfig] = useState<{
    weight_distance: number;
    weight_workload: number;
    weight_profitability: number;
    timeout_default_minutes: number;
    max_suggestions_per_move: number;
    min_score_threshold: number;
    distance_max_miles: number;
    auto_assign_enabled: boolean;
  } | null>(null);
  const [configStats, setConfigStats] = useState<{
    total_suggestions: number;
    pending_suggestions: number;
    confirmed_suggestions: number;
    expired_suggestions: number;
    avg_score: number;
  } | null>(null);
  const [profitabilityGoals, setProfitabilityGoals] = useState<Array<{
    id: number;
    region: string | null;
    driver_type: string | null;
    daily_goal_amount: number;
  }>>([]);

  // Background refresh state
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const loadOrdersRef = useRef<(isBackgroundRefresh?: boolean) => Promise<void>>(null!);

  // Helper component to render Pro# as a clickable link to legacy system
  const ProNumberLink: React.FC<{ pronumber: number | string }> = ({ pronumber }) => {
    const legacyCmsUrl = config.LEGACY_CMS_URL;

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      e.stopPropagation();
      markViewed(pronumber);
      const url = `${legacyCmsUrl}/index_cms.pl?action=savecustomsearch&contlist=${pronumber}&searchtype=pronumber`;
      window.open(url, '_blank', 'noopener,noreferrer');
    };

    return (
      <a
        href={`${legacyCmsUrl}/index_cms.pl?action=savecustomsearch&contlist=${pronumber}&searchtype=pronumber`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
        onClick={handleClick}
        onMouseDown={(e) => e.stopPropagation()}
        title="Open in legacy CMS"
      >
        <ArrowTopRightOnSquareIcon className="h-4 w-4" />
      </a>
    );
  };

  // Open order details modal
  const openOrderModal = (order: Order) => {
    setSelectedOrder(order);
    setIsOrderModalOpen(true);
  };

  // Close order details modal
  const closeOrderModal = () => {
    setSelectedOrder(null);
    setIsOrderModalOpen(false);
  };

  // Load current config for display bar
  const loadCurrentConfig = useCallback(async (region?: string) => {
    try {
      const response = await api.get(`/smart-dispatch/config${region ? `?region=${encodeURIComponent(region)}` : ''}`);
      const configData = response.data.config || {};

      // Helper to get effective value (region override or global default)
      const getValue = (key: string, defaultValue: string): string => {
        const item = configData[key];
        if (!item) return defaultValue;
        if (region && item.overrides) {
          const override = item.overrides.find((o: any) => o.region === region);
          if (override) return override.value;
        }
        return item.value || defaultValue;
      };

      setCurrentConfig({
        weight_distance: parseFloat(getValue('weight_distance', '0.40')),
        weight_workload: parseFloat(getValue('weight_workload', '0.30')),
        weight_profitability: parseFloat(getValue('weight_profitability', '0.30')),
        timeout_default_minutes: parseInt(getValue('timeout_default_minutes', '30'), 10),
        max_suggestions_per_move: parseInt(getValue('max_suggestions_per_move', '3'), 10),
        min_score_threshold: parseFloat(getValue('min_score_threshold', '0.3')),
        distance_max_miles: parseInt(getValue('distance_max_miles', '50'), 10),
        auto_assign_enabled: getValue('auto_assign_enabled', 'false') === 'true',
      });
    } catch (err) {
      console.error('Failed to load config:', err);
    }
  }, []);

  // Load config stats
  const loadConfigStats = useCallback(async () => {
    try {
      const response = await api.get('/smart-dispatch/stats');
      setConfigStats(response.data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, []);

  // Load profitability goals
  const loadProfitabilityGoals = useCallback(async () => {
    try {
      const response = await api.get('/smart-dispatch/profitability-goals');
      setProfitabilityGoals(response.data.goals || []);
    } catch (err) {
      console.error('Failed to load profitability goals:', err);
    }
  }, []);

  // Load config on mount and when configRegion changes
  useEffect(() => {
    loadCurrentConfig(configRegion || undefined);
  }, [configRegion, loadCurrentConfig]);

  // Load stats on mount
  useEffect(() => {
    loadConfigStats();
  }, [loadConfigStats]);

  // Build filter options from loaded orders data (regions and customers)
  // Only update when no filters are applied to avoid losing options when filtering
  useEffect(() => {
    if (orders.length === 0) return;

    // Only update options when completely unfiltered to preserve the full list
    const isUnfiltered = selectedRegions.length === 0 && selectedCustomers.length === 0;
    if (!isUnfiltered) return;

    const uniqueRegions = [...new Set(orders.map(o => o.region).filter((r): r is string => Boolean(r)))].sort();
    setRegionOptions(
      uniqueRegions.map(r => ({
        value: String(r),
        label: String(r),
      }))
    );

    const uniqueCustomers = [...new Set(orders.map(o => o.customerName).filter((c): c is string => Boolean(c)))].sort();
    setCustomerOptions(
      uniqueCustomers.map(c => ({
        value: c,
        label: c,
      }))
    );
  }, [orders, selectedRegions.length, selectedCustomers.length]);

  // Load available orders and suggestions
  const loadOrders = useCallback(async (isBackgroundRefresh = false) => {
    if (!isBackgroundRefresh) {
      setLoading(true);
    }
    // The saved selection is part of the request; going early just fetches twice.
    if (!fieldsReady) return;

    try {
      // Build query params from filters to reduce data fetched
      const params = new URLSearchParams();

      // Region filter - only pass if filtering (not "all regions")
      if (selectedRegions.length > 0) {
        // Backend expects single region, pass first selected
        // Note: If multiple regions selected, we still filter client-side
        if (selectedRegions.length === 1) {
          params.append('region', selectedRegions[0]);
        }
      }

      // Order Dispatcher filter - expand groups and pass first if single
      const expandedOrderDispatchers = selectedOrderDispatchers.flatMap(d =>
        d.includes('~') ? d.split('~') : [d]
      );
      if (expandedOrderDispatchers.length === 1) {
        params.append('dispatcher', expandedOrderDispatchers[0]);
      }

      const queryString = params.toString();
      // The saved selection rides on the ORDERS feed only; suggestions carry no
      // order columns of their own.
      const ordersParams = new URLSearchParams(queryString);
      if (additionalFieldsKey) ordersParams.append('additional_fields', additionalFieldsKey);
      const ordersQuery = ordersParams.toString();
      const ordersUrl = `/orders/available-pros-all${ordersQuery ? `?${ordersQuery}` : ''}`;
      const suggestionsUrl = `/smart-dispatch/suggestions${queryString ? `?${queryString}` : ''}`;

      // Fetch both orders and suggestions in parallel
      const [ordersResponse, suggestionsResponse] = await Promise.all([
        api.get(ordersUrl),
        api.get(suggestionsUrl).catch(err => {
          console.warn('Smart dispatch suggestions not available:', err);
          return { data: { data: [] } };
        }),
      ]);

      const availableOrders = ordersResponse.data?.data || [];
      const fetchedSuggestions: SmartDispatchSuggestion[] = suggestionsResponse.data?.data || [];

      // Store suggestions
      setSuggestions(fetchedSuggestions);

      // Build a map of top suggestions by order key for pre-populating drivers
      const topSuggestionMap = new Map<string, string>();
      fetchedSuggestions.forEach(s => {
        const key = `${s.pronumber}-${s.legnum}`;
        // Only use rank 1 (top suggestion) for pre-populating
        if (s.suggestion_rank === 1) {
          topSuggestionMap.set(key, s.suggested_driver);
        }
      });

      // The keys the backend attaches, which is what a generated column reads. This
      // map rebuilds each row field by field, so anything not named below is dropped
      // before the grid ever sees it — derived from additionalFieldsKey rather than
      // the array so it needs no extra dependency.
      const attachedKeys = additionalFieldsKey
        ? additionalFieldsKey.split(',').map(getApiFieldName)
        : [];

      // Map the response to match Order interface
      // Note: Backend returns data pre-sorted by priority (cutoff, LFD, per diem, etc.)
      const mappedOrders: Order[] = availableOrders.map((order: any) => {
        const orderKey = `${order.pronumber}-${order.legnum || 0}`;
        const suggestedDriver = topSuggestionMap.get(orderKey) || '';
        const attached: Record<string, unknown> = {};
        for (const key of attachedKeys) attached[key] = order[key];

        return {
          ...attached,
          pronumber: order.pronumber,
          legnum: order.legnum || 0,
          billToName: order.billToName || '',
          customerName: order.customerName || '',
          keyAccount: '',
          dispatcher: order.dispatcher || '',
          region: order.region || '',
          appointmentDate: order.destApptDate || '',
          originAppointmentDate: order.origApptDate || order.destApptDate || '',
          originName: order.originName || '',
          originCityState: order.originCityState || '',
          originCoordinates: order.originCoordinates || '',
          origcity: order.originCity || '',
          origstate: order.originState || '',
          origzip: order.originZip || '',
          origcode: order.originCode || 0,
          destcode: order.destCode || 0,
          destinationAppointmentDate: order.destApptDate || '',
          destinationName: order.destName || '',
          destinationCityState: order.destCityState || '',
          destinationCoordinates: order.destCoordinates || '',
          destcity: order.destCityState?.split(',')[0]?.trim() || '',
          deststate: order.destCityState?.split(',')[1]?.trim() || '',
          destzip: '',
          driver: suggestedDriver, // Pre-populate with suggested driver
          container: order.container || '',
          size: order.size || '',
          equipType: order.equipType || '',
          chassisId: '',
          chassisType: order.chassisType || '',
          chassisProvider: '',
          scac: '',
          cutoffDatetime: order.cutoffDatetime || '',
          documentCutoffDatetime: '',
          lfdDate: order.lfdDate || '',
          custTargetDate: '',
          perDiemDate: order.perDiemDate || '',
          firstRcvDate: '',
          legType: '',
          hasPickup: false,
          hasDelivery: false,
          hasTerminate: false,
          isMultiStop: false,
          legs: [], // Required by Order interface, but we use dispatchScopeLegs for miles
          dispatchScopeLegs: order.legs || [], // Legs in dispatch scope (from current leg until drop='Y')
          hot: order.hot || 'N',
          isDaycab: order.isDaycab || 'N',
          hazmat: order.hazmat || 'N',
          isTank: order.isTank || 'N',
          overweightPermitRequired: order.overweightPermitRequired || 'N',
          otr: '',
          rescheduleCount: Number(order.rescheduleCount) || 0,
          isEarly: order.isEarly || false,
          isLiveLoad: false,
          destApptTime1: order.destApptTime1 || '',
          destApptTime2: order.destApptTime2 || '',
          warnings: order.warnings || [],
          offhire: order.offhire || 'N',
          redeliveryExpirationDate: order.redeliveryExpirationDate || '',
          openTtCount: order.openTtCount,
          openTickets: order.openTickets,
          openRoCount: order.openRoCount,
        };
      });

      // Show all orders, even if missing appointment dates
      setOrders(mappedOrders);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to load orders:', error);
    } finally {
      if (!isBackgroundRefresh) {
        setLoading(false);
      }
    }
  }, [selectedRegions, selectedOrderDispatchers, fieldsReady, additionalFieldsKey]);

  // Keep ref updated with latest loadOrders function
  loadOrdersRef.current = loadOrders;

  // Initial load once the saved global filters have restored (still exactly one
  // fire — the gate flips false once; before this it loaded with DEFAULT filters
  // and showed unfiltered data until the first background poll corrected it).
  // Note: loadOrders itself stays out of deps via the ref to prevent refetch
  // loops when filter state changes (home region auto-select, etc.).
  useEffect(() => {
    if (globalFiltersLoading) return;
    loadOrdersRef.current(false);
  }, [globalFiltersLoading]);

  // Background refresh - polls for updated suggestions from the background job
  // Uses ref to always call the latest loadOrders with current filter state
  useVisibilityAwareInterval(() => {
    loadOrdersRef.current(true);
  }, BACKGROUND_REFRESH_INTERVAL);

  // Helper to expand dispatcher groups (values containing ~) into individual usernames
  const expandDispatcherGroups = (dispatchers: string[]): string[] => {
    const expanded: string[] = [];
    for (const d of dispatchers) {
      if (d.includes('~')) {
        expanded.push(...d.split('~'));
      } else {
        expanded.push(d);
      }
    }
    return expanded;
  };

  // Filter orders based on selected filters
  // Order Dispatcher filters by order.dispatcher
  // Driver Dispatcher filters by suggested driver's dispatcher (only show orders where top suggestion's driver belongs to that dispatcher)
  const filteredOrders = useMemo(() => {
    // Expand any dispatcher groups for filtering
    const expandedOrderDispatchers = expandDispatcherGroups(selectedOrderDispatchers);
    const expandedDriverDispatchers = expandDispatcherGroups(selectedDriverDispatchers);

    return orders.filter(order => {
      if (expandedOrderDispatchers.length > 0 && !expandedOrderDispatchers.includes(order.dispatcher || '')) {
        return false;
      }
      if (selectedRegions.length > 0 && !selectedRegions.includes(String(order.region || ''))) {
        return false;
      }
      if (selectedCustomers.length > 0 && !selectedCustomers.includes(order.customerName || '')) {
        return false;
      }
      // If driver dispatcher filter is applied, only show orders where the top suggestion's driver belongs to that dispatcher
      if (expandedDriverDispatchers.length > 0) {
        const orderKey = `${order.pronumber}-${order.legnum || 0}`;
        const orderSuggestions = suggestionsByOrder.get(orderKey);
        // Get top suggestion (rank 1)
        const topSuggestion = orderSuggestions?.find(s => s.suggestion_rank === 1);
        if (!topSuggestion || !expandedDriverDispatchers.includes(topSuggestion.driver_dispatcher || '')) {
          return false;
        }
      }
      return true;
    });
  }, [orders, selectedOrderDispatchers, selectedRegions, selectedCustomers, selectedDriverDispatchers, suggestionsByOrder]);

  // Group orders by region while preserving backend priority sort order
  const ordersByRegion = useMemo(() => {
    const grouped: { [region: string]: Order[] } = {};

    // Keep the backend's priority sort order - do NOT re-sort here
    // Backend already sorted by: cutoff time, LFD, per diem, appointment, etc.
    filteredOrders.forEach(order => {
      const regionKey = order.region || 'Unknown';
      if (!grouped[regionKey]) {
        grouped[regionKey] = [];
      }
      grouped[regionKey].push(order);
    });

    return grouped;
  }, [filteredOrders]);

  // Transform orders to SmartDispatchRow format with sub-rows for connected moves
  const transformedOrdersByRegion = useMemo(() => {
    const result: { [region: string]: SmartDispatchRow[] } = {};

    Object.entries(ordersByRegion).forEach(([region, orders]) => {
      result[region] = orders.map(order => {
        const orderKey = `${order.pronumber}-${order.legnum || 0}`;
        const hasMultipleLegs = order.dispatchScopeLegs && order.dispatchScopeLegs.length > 1;

        // Create the parent row
        const parentRow: SmartDispatchRow = {
          ...order,
          id: orderKey,
          isSubRow: false,
        };

        // If multiple legs, create sub-rows (skip first leg since it's shown on the parent)
        if (hasMultipleLegs && order.dispatchScopeLegs) {
          parentRow.subLegs = order.dispatchScopeLegs.slice(1).map((leg, idx) => ({
            ...order,
            id: `${orderKey}-leg${leg.legnum}`,
            isSubRow: true,
            legnum: leg.legnum,
            legOriginName: leg.originName,
            legOriginCityState: leg.originCityState,
            legDestName: leg.destName,
            legDestCityState: leg.destCityState,
            legMiles: typeof leg.legMiles === 'string' ? parseFloat(leg.legMiles) : leg.legMiles,
            legDrop: leg.legDrop,
            // Clear sub-rows on leg rows to prevent infinite nesting
            subLegs: undefined,
          }));
        }

        return parentRow;
      });
    });

    return result;
  }, [ordersByRegion]);

  // Handle driver assignment from DriverSelectorCell
  const handleDriverAssigned = useCallback((pronumber: number, legnum: number, driverUnit: string) => {
    const orderKey = `${pronumber}-${legnum}`;
    setDriverAssignments(prev => ({
      ...prev,
      [orderKey]: driverUnit,
    }));

    // Update the order in state with the new driver
    setOrders(prevOrders =>
      prevOrders.map(order => {
        if (order.pronumber === pronumber && (order.legnum || 0) === legnum) {
          return { ...order, driver: driverUnit };
        }
        return order;
      })
    );
  }, []);

  // Handle driver validation status change from DriverSelectorCell
  // Note: This is currently not called from DriverSelectorCell to prevent infinite loops
  // The validation status is only used locally in the cell for visual feedback
  const handleValidationChange = useCallback((pronumber: number, legnum: number, isQualified: boolean | null) => {
    const orderKey = `${pronumber}-${legnum}`;
    setDriverQualifications(prev => ({
      ...prev,
      [orderKey]: isQualified,
    }));
  }, []);

  // Handle confirm single order
  const handleConfirmOrder = useCallback(async (order: Order) => {
    const orderKey = `${order.pronumber}-${order.legnum || 0}`;
    // Use ref to avoid recreating callback when driverAssignments changes
    const assignedDriver = driverAssignmentsRef.current[orderKey] || order.driver;

    if (!assignedDriver) {
      showToast({
        type: 'error',
        message: 'No driver assigned to confirm',
      });
      return;
    }

    // Set pending state
    setPendingConfirms(prev => new Set([...prev, orderKey]));

    try {
      // Get today's date in YYYY-MM-DD format for appointment dates
      const now = new Date();
      const today = now.toISOString().split('T')[0];
      const currentYear = now.getFullYear();

      // Helper to check if a date is missing/invalid
      const isMissingDate = (date: string | undefined) => {
        return !date || date.trim() === '' || date === '0000-00-00' || date === '-';
      };

      // Helper to convert MM/DD or MM/DD/YY format to YYYY-MM-DD
      const formatDateForApi = (date: string | undefined): string => {
        if (isMissingDate(date)) {
          return today;
        }

        // Check if already in YYYY-MM-DD format
        if (date!.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return date!;
        }

        // Parse MM/DD or MM/DD/YY format
        const parts = date!.split('/');
        if (parts.length >= 2) {
          const month = parts[0].padStart(2, '0');
          const day = parts[1].padStart(2, '0');
          let year = currentYear.toString();

          if (parts.length === 3) {
            // MM/DD/YY format - convert 2-digit year to 4-digit
            const shortYear = parseInt(parts[2], 10);
            year = (shortYear < 50 ? 2000 + shortYear : 1900 + shortYear).toString();
          }

          return `${year}-${month}-${day}`;
        }

        // Fallback to today if parsing fails
        return today;
      };

      // Build leg data for all legs in the dispatch scope
      // The order.dispatchScopeLegs array contains all legs from current leg until a legDrop='Y'
      // This ensures driver is assigned to all grouped legs (e.g., when drop='N')
      const legData = (order.dispatchScopeLegs && order.dispatchScopeLegs.length > 0)
        ? order.dispatchScopeLegs.map(leg => ({
            legnum: leg.legnum,
            origcode: leg.origcode,
            destcode: leg.destcode,
            driver: assignedDriver,
            origApptDate: formatDateForApi(leg.origApptDate),
            destApptDate: formatDateForApi(leg.destApptDate),
            sendToGwiMove: true,
            // Include original values for stale data checking
            // NOTE: Use raw leg values here, NOT formatDateForApi which converts empty dates to today
            old: {
              origcode: leg.origcode,
              destcode: leg.destcode,
              driver: leg.driver || '',
              origApptDate: leg.origApptDate || '',
              destApptDate: leg.destApptDate || '',
            },
          }))
        : [{
            // Fallback to single leg if dispatchScopeLegs array not available
            legnum: order.legnum || 0,
            origcode: order.origcode || 0,
            destcode: order.destcode || 0,
            driver: assignedDriver,
            origApptDate: formatDateForApi(order.originAppointmentDate),
            destApptDate: formatDateForApi(order.destinationAppointmentDate),
            sendToGwiMove: true,
            // Include original values for stale data checking
            // NOTE: Use raw order values here, NOT formatDateForApi which converts empty dates to today
            old: {
              origcode: order.origcode || 0,
              destcode: order.destcode || 0,
              driver: order.driver || '',
              origApptDate: order.originAppointmentDate || '',
              destApptDate: order.destinationAppointmentDate || '',
            },
          }];

      // Assign the driver via updateLegDetails API
      await ordersService.updateLegDetails(order.pronumber, legData);

      // Get the suggestion for this order to update suggestion status
      const orderSuggestions = suggestionsByOrder.get(orderKey);
      const topSuggestion = orderSuggestions?.find(s => s.suggested_driver === assignedDriver);

      if (topSuggestion) {
        // Confirm the suggestion via API to update suggestion tracking (fire-and-forget)
        // This is just for analytics/tracking - the actual assignment already happened via updateLegDetails
        // 5 second timeout to prevent hanging requests
        api.post(`/smart-dispatch/suggestions/${topSuggestion.id}/confirm`, {}, { timeout: 5000 }).catch(suggestionError => {
          console.warn('Failed to update suggestion status:', suggestionError);
        });
      }

      // Mark as confirmed locally
      setConfirmedMoves(prev => new Set([...prev, orderKey]));

      // Show success message indicating how many legs were assigned
      const legsAssigned = legData.length;
      showToast({
        type: 'success',
        message: legsAssigned > 1
          ? `Driver ${assignedDriver} assigned to ${order.pronumber} (${legsAssigned} legs)`
          : `Driver ${assignedDriver} assigned to ${order.pronumber}-${order.legnum || 0}`,
      });

      // Refresh orders and suggestions to get updated data
      await loadOrders(true);
    } catch (error: any) {
      console.error('Failed to confirm order:', error);
      showToast({
        type: 'error',
        message: error?.response?.data?.detail || error?.message || 'Failed to assign driver',
      });
    } finally {
      // Clear pending state
      setPendingConfirms(prev => {
        const next = new Set(prev);
        next.delete(orderKey);
        return next;
      });
    }
  // Note: driverAssignments accessed via ref to prevent callback recreation
  }, [suggestionsByOrder, loadOrders, showToast]);

  // Manual refresh handler
  const handleManualRefresh = useCallback(() => {
    loadOrders(false);
  }, [loadOrders]);

  // Handle suggest alternative
  const handleSuggestAlternative = useCallback(async (order: Order) => {
    const orderKey = `${order.pronumber}-${order.legnum || 0}`;
    // Use ref to avoid recreating callback when driverAssignments changes
    const currentDriver = driverAssignmentsRef.current[orderKey] || order.driver;

    setAlternativesOrder(order);
    setLoadingAlternatives(true);
    setIsAlternativesDrawerOpen(true);
    setShowDisqualified(false);

    try {
      const response = await api.post('/smart-dispatch/suggestions/suggest-alternatives', null, {
        params: {
          pronumber: order.pronumber,
          legnum: order.legnum || 0,
          exclude_driver: currentDriver || undefined,
        },
      });

      setAlternativeSuggestions(response.data?.alternatives || []);
      setDisqualifiedDrivers(response.data?.disqualified_drivers || []);
      setTotalDriversChecked(response.data?.total_drivers_checked || 0);
      setDebugInfo(response.data?.debug_info || null);
    } catch (error) {
      console.error('Failed to get alternative suggestions:', error);
      setAlternativeSuggestions([]);
      setDisqualifiedDrivers([]);
      setTotalDriversChecked(0);
    } finally {
      setLoadingAlternatives(false);
    }
  // Note: driverAssignments accessed via ref to prevent callback recreation
  }, []);

  // Handle show on map
  const handleShowOnMap = useCallback((order: Order) => {
    setSelectedOrderForMap(order);
    setIsDrawerOpen(true);
    // Also fetch nearby drivers for the map
    driverDrawer.openDrawer(order, order.legnum);
  }, [driverDrawer]);

  // Handle driver selection from map drawer
  const handleMapDriverSelect = useCallback((driver: EnrichedDriver) => {
    if (!selectedOrderForMap) return;

    const key = `${selectedOrderForMap.pronumber}-${selectedOrderForMap.legnum || 0}`;

    // Update the driver assignment
    setDriverAssignments(prev => ({
      ...prev,
      [key]: driver.unitNumber,
    }));

    // Close the drawer
    setIsDrawerOpen(false);
    driverDrawer.closeDrawer();

    showToast({ message: `Driver ${driver.unitNumber} assigned to PRO ${selectedOrderForMap.pronumber}`, type: 'success' });
  }, [selectedOrderForMap, driverDrawer, showToast]);

  // Build route stops from selected order for map display
  const routeStopsForMap = useMemo((): RouteStop[] => {
    if (!selectedOrderForMap) return [];

    const legs = selectedOrderForMap.dispatchScopeLegs;

    // If we have dispatch scope legs, use them to build the route
    if (legs && legs.length > 0) {
      const stops: RouteStop[] = [];

      legs.forEach((leg, index) => {
        const isFirst = index === 0;
        const isLast = index === legs.length - 1;

        // Add origin of first leg
        if (isFirst) {
          stops.push({
            name: leg.originName || 'Origin',
            cityState: leg.originCityState,
            coordinates: leg.originCoordinates || selectedOrderForMap.originCoordinates || '',
            type: 'origin',
            legnum: leg.legnum,
          });
        }

        // Add destination of each leg
        // For intermediate legs, this becomes a "stop"
        // For the last leg, this is the final "destination"
        stops.push({
          name: leg.destName || 'Destination',
          cityState: leg.destCityState,
          coordinates: leg.destCoordinates || (isLast ? selectedOrderForMap.destinationCoordinates : '') || '',
          type: isLast ? 'destination' : 'stop',
          legnum: leg.legnum,
        });
      });

      // Filter out stops without coordinates
      return stops.filter(s => s.coordinates);
    }

    // Fallback: simple origin -> destination
    return [
      {
        name: selectedOrderForMap.originName || 'Origin',
        cityState: `${selectedOrderForMap.origcity}, ${selectedOrderForMap.origstate}`,
        coordinates: selectedOrderForMap.originCoordinates || '',
        type: 'origin',
      },
      {
        name: selectedOrderForMap.destinationName || 'Destination',
        cityState: `${selectedOrderForMap.destcity}, ${selectedOrderForMap.deststate}`,
        coordinates: selectedOrderForMap.destinationCoordinates || '',
        type: 'destination',
      },
    ];
  }, [selectedOrderForMap]);

  // Format last refresh time
  const formatLastRefresh = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  // Set up footer button
  useEffect(() => {
    setDispatchButton(
      <div className="flex items-center gap-4">
        {/* Last refresh indicator */}
        <div className="flex items-center gap-2 text-sm text-muted">
          <span>Last updated: {formatLastRefresh(lastRefresh)}</span>
          <button
            onClick={handleManualRefresh}
            disabled={loading}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
            title="Refresh now"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
    );

    return () => setDispatchButton(null);
  }, [loading, lastRefresh, handleManualRefresh, setDispatchButton]);

  // Get top suggestion for an order
  const getTopSuggestion = useCallback((pronumber: number, legnum: number): SmartDispatchSuggestion | null => {
    const key = `${pronumber}-${legnum}`;
    const orderSuggestions = suggestionsByOrder.get(key);
    return orderSuggestions?.[0] || null;
  }, [suggestionsByOrder]);

  // Score tooltip content component
  const ScoreTooltip: React.FC<{ suggestion: SmartDispatchSuggestion }> = ({ suggestion }) => (
    <div className="text-xs space-y-1 min-w-[180px]">
      <div className="font-semibold border-b border-rule pb-1 mb-1">
        Score Breakdown
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500">Distance:</span>
        <span>{formatScore(suggestion.score_distance)}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500">Workload:</span>
        <span>{formatScore(suggestion.score_workload)}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500">Profitability:</span>
        <span>{formatScore(suggestion.score_profitability)}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500">Qualification:</span>
        <span>{formatScore(suggestion.score_qualification)}</span>
      </div>
      <div className="border-t border-rule pt-1 mt-1">
        <div className="flex justify-between text-gray-500">
          <span>Distance to pickup:</span>
          <span>{suggestion.distance_to_origin_miles?.toFixed(1)} mi</span>
        </div>
        <div className="flex justify-between text-gray-500">
          <span>Remaining dispatches:</span>
          <span>{suggestion.driver_remaining_dispatches}</span>
        </div>
      </div>
    </div>
  );

  // TanTable column definitions for Smart Dispatch
  const baseSmartDispatchColumns: ColumnDef<SmartDispatchRow>[] = useMemo(() => {
  return [
    {
      id: 'proLeg',
      accessorKey: 'pronumber',
      header: 'PRO#',
      size: 120,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: ({ row }) => {
        const order = row.original;
        const isSubRow = order.isSubRow;

        if (isSubRow) {
          return (
            <span className="text-xs text-muted">
              ↳ Leg {order.legnum}
            </span>
          );
        }

        // Get PRO# button class based on reschedule count (red for 2+, orange for 1)
        const rescheduleCount = Number(order.rescheduleCount) || 0;
        const proButtonClass = rescheduleCount >= 2
          ? 'bg-red-500 text-white px-2 py-0.5 rounded-md font-semibold'
          : rescheduleCount === 1
          ? 'bg-orange-500 text-white px-2 py-0.5 rounded-md font-semibold'
          : 'font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline';

        const viewed = isViewed(order.pronumber);
        return (
          <div
            className={`flex items-center gap-1 px-1 rounded transition-colors duration-150 ${
              viewed ? 'bg-green-200 dark:bg-green-600/50' : ''
            }`}
            title={viewed ? 'Viewed' : (rescheduleCount > 0 ? `Service failure count: ${rescheduleCount}` : undefined)}
          >
            <button
              onClick={() => {
                markViewed(order.pronumber);
                openOrderModal(order);
              }}
              className={proButtonClass}
              title={rescheduleCount > 0 ? `Service failure count: ${rescheduleCount}` : undefined}
            >
              {order.pronumber}-{order.legnum || 0}
            </button>
            <ProNumberLink pronumber={order.pronumber} />
          </div>
        );
      },
    },
    {
      id: 'customer',
      accessorKey: 'customerName',
      header: 'Customer',
      size: 150,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: ({ row }) => {
        if (row.original.isSubRow) return null;
        return (
          <span className="truncate" title={row.original.customerName || ''}>
            {row.original.customerName}
          </span>
        );
      },
    },
    {
      id: 'origin',
      accessorKey: 'originName',
      header: 'Origin',
      size: 150,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: ({ row }) => {
        const order = row.original;
        if (order.isSubRow) {
          return (
            <div>
              <div className="truncate text-xs" title={order.legOriginName || ''}>
                {order.legOriginName || 'TBD'}
              </div>
              <div className="text-xs text-gray-400">{order.legOriginCityState || ''}</div>
            </div>
          );
        }
        return (
          <div>
            <div className="truncate" title={order.originName || ''}>{order.originName}</div>
            <div className="text-xs text-gray-400">{order.origcity}, {order.origstate}</div>
          </div>
        );
      },
    },
    {
      id: 'destination',
      accessorKey: 'destinationName',
      header: 'Destination',
      size: 150,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: ({ row }) => {
        const order = row.original;
        if (order.isSubRow) {
          return (
            <div>
              <div className="truncate text-xs" title={order.legDestName || ''}>
                {order.legDestName || 'TBD'}
                {order.legDrop === 'Y' && <span className="ml-1 text-orange-500">(drop)</span>}
              </div>
              <div className="text-xs text-gray-400">{order.legDestCityState || ''}</div>
            </div>
          );
        }
        return (
          <div>
            <div className="truncate" title={order.destinationName || ''}>{order.destinationName}</div>
            <div className="text-xs text-gray-400">{order.destcity}, {order.deststate}</div>
          </div>
        );
      },
    },
    {
      id: 'appt',
      header: 'Appt',
      size: 90,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      accessorFn: (row) => row.destApptTime1 || '-',  // Return '-' for empty so it's filterable
      cell: ({ row }) => {
        const order = row.original;
        if (order.isSubRow) return null;
        if (!order.destApptTime1) return <span className="text-gray-400">-</span>;
        // Format like At Risk Monitor: "08:00-10:00" or just "08:00" if same
        const apptTime = order.destApptTime2 && order.destApptTime2 !== order.destApptTime1
          ? `${order.destApptTime1}-${order.destApptTime2}`
          : order.destApptTime1;
        return <span className="text-sm text-body">{apptTime}</span>;
      },
    },
    {
      id: 'miles',
      header: 'Miles',
      size: 80,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      accessorFn: (row) => {
        const miles = row.isSubRow ? row.legMiles : getOrderMiles(row);
        return miles || '-';  // Return '-' for null/zero so it's filterable
      },
      cell: ({ row }) => {
        const order = row.original;
        if (order.isSubRow && order.legMiles) {
          return <span className="text-xs">{order.legMiles.toLocaleString()} mi</span>;
        }
        const miles = getOrderMiles(order);
        return miles ? `${miles.toLocaleString()} mi` : '-';
      },
      meta: {
        filterValueLabel: (miles: string) => {
          if (miles === '-' || miles === '' || miles === '0') return '-';
          const m = parseFloat(miles);
          if (isNaN(m) || m === 0) return '-';
          return `${m.toLocaleString()} mi`;
        },
      },
    },
    {
      id: 'estTime',
      header: 'Est. Time',
      size: 90,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      accessorFn: (row) => {
        const miles = row.isSubRow ? row.legMiles : getOrderMiles(row);
        return miles ? miles / 50 : '-';  // Return '-' for empty so it's filterable
      },
      cell: ({ row }) => {
        const order = row.original;
        if (order.isSubRow && order.legMiles) {
          return <span className="text-xs">{getEstimatedTripTime(order.legMiles)}</span>;
        }
        const miles = getOrderMiles(order);
        return getEstimatedTripTime(miles);
      },
      meta: {
        // Convert hours back to formatted time string for filter dropdown display
        filterValueLabel: (hours: string) => {
          if (hours === '-') return '-';
          const h = parseFloat(hours);
          if (isNaN(h) || h === 0) return '-';
          if (h < 1) {
            return `${Math.round(h * 60)}m`;
          }
          const wholeHours = Math.floor(h);
          const minutes = Math.round((h - wholeHours) * 60);
          if (minutes === 0) {
            return `${wholeHours}h`;
          }
          return `${wholeHours}h ${minutes}m`;
        },
      },
    },
    {
      id: 'cutoff',
      header: 'Cutoff / Per Diem',
      size: 110,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      accessorFn: (row) => row.cutoffDatetime || row.perDiemDate || '-',  // Return '-' for empty so it's filterable
      cell: ({ row }) => {
        const order = row.original;
        if (order.isSubRow) return null;
        if (order.cutoffDatetime) {
          return <span className="text-orange-600 dark:text-orange-400">{formatDate(order.cutoffDatetime)}</span>;
        }
        if (order.perDiemDate) {
          return <span className="text-blue-600 dark:text-blue-400">{formatDate(order.perDiemDate)}</span>;
        }
        return '-';
      },
    },
    {
      id: 'requirements',
      header: 'Req',
      size: 100,
      cell: ({ row }) => {
        const order = row.original;
        if (order.isSubRow) return null;
        return (
          <div className="flex gap-1 items-center flex-wrap">
            {order.hot === 'Y' && (
              <Tooltip content="HOT Load" placement="top">
                <span className="gw-icon gw-icon-hotjar text-brand-red cursor-help" style={{ fontSize: '14px' }}></span>
              </Tooltip>
            )}
            {order.hazmat === 'Y' && (
              <Tooltip content="Hazmat Required" placement="top">
                <span className="flaticon flaticon-warning text-orange-500 cursor-help" style={{ fontSize: '14px' }}></span>
              </Tooltip>
            )}
            {order.overweightPermitRequired === 'Y' && (
              <Tooltip content="Overweight Permit" placement="top">
                <span className="flaticon flaticon-weight text-heading cursor-help" style={{ fontSize: '14px' }}></span>
              </Tooltip>
            )}
            {order.isEarly && (
              <Tooltip content="Early Appointment" placement="top">
                <span className="gw-icon gw-icon-early text-heading cursor-help" style={{ fontSize: '14px' }}></span>
              </Tooltip>
            )}
            {order.chassisType?.includes('93K') && (
              <Tooltip content="93K Triaxle Chassis" placement="top">
                <span className="gw-icon cursor-help text-heading" style={{ fontSize: '14px' }}>&#xE9E5;</span>
              </Tooltip>
            )}
            {!order.chassisType?.includes('93K') && order.chassisType?.toLowerCase().includes('triaxle') && (
              <Tooltip content="Triaxle Chassis" placement="top">
                <span className="gw-icon gw-icon-dual-axle text-blue-600 cursor-help" style={{ fontSize: '14px' }}></span>
              </Tooltip>
            )}
            {order.equipType?.toLowerCase().includes('swing') && (
              <Tooltip content="Swing" placement="top">
                <span className="flaticon flaticon-cargo text-purple-600 cursor-help" style={{ fontSize: '16px' }}></span>
              </Tooltip>
            )}
            {order.isTank === 'Y' && (
              <Tooltip content="Tank Endorsement" placement="top">
                <span className="flaticon flaticon-oil-tank text-purple-600 cursor-help" style={{ fontSize: '14px' }}></span>
              </Tooltip>
            )}
            {order.warnings && order.warnings.length > 0 && order.warnings.map((w, idx) => (
              <Tooltip key={idx} content={w.message} placement="top">
                <ExclamationTriangleIcon className="h-4 w-4 text-red-500 cursor-help" />
              </Tooltip>
            ))}
          </div>
        );
      },
    },
    {
      id: 'score',
      header: 'Score',
      size: 70,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      accessorFn: (row) => {
        const topSuggestion = suggestionsByOrder.get(`${row.pronumber}-${row.legnum || 0}`)?.[0];
        return topSuggestion?.suggestion_score ?? 0;
      },
      cell: ({ row }) => {
        const order = row.original;
        if (order.isSubRow) return null;
        const topSuggestion = getTopSuggestion(order.pronumber, order.legnum || 0);
        if (!topSuggestion) return <span className="text-xs text-gray-400">-</span>;
        return (
          <Tooltip
            content={
              <div className="bg-panel rounded-lg shadow-lg border border-rule p-2">
                <ScoreTooltip suggestion={topSuggestion} />
              </div>
            }
            placement="top"
          >
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium cursor-help ${getScoreColor(topSuggestion.suggestion_score)}`}>
              {formatScore(topSuggestion.suggestion_score)}
              <InformationCircleIcon className="h-3 w-3 ml-1 opacity-60" />
            </span>
          </Tooltip>
        );
      },
      meta: {
        filterValueLabel: (score: string) => {
          const s = parseFloat(score);
          if (isNaN(s) || s === 0) return '-';
          return `${Math.round(s * 100)}%`;
        },
      },
    },
    {
      id: 'driver',
      accessorKey: 'driver',
      header: 'Driver',
      size: 180,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: ({ row }) => {
        const order = row.original;
        if (order.isSubRow) return null;
        const orderKey = `${order.pronumber}-${order.legnum || 0}`;
        const isConfirmed = confirmedMoves.has(orderKey);
        // Use manual assignment if exists (including empty string to clear), otherwise fall back to order.driver (suggestion)
        // Access via ref to avoid column recreation when assignments change
        const assignments = driverAssignmentsRef.current;
        const hasManualAssignment = orderKey in assignments;
        const currentDriver = hasManualAssignment ? assignments[orderKey] : order.driver;
        // Determine initial validation status:
        // 1. If we have a stored validation result (from previous manual validation), use it
        // 2. If driver came from suggestion (not manual assignment), it's pre-validated as qualified
        // 3. Otherwise null (needs validation)
        const storedQualification = driverQualificationsRef.current[orderKey];
        let initialValidationStatus: 'valid' | 'invalid' | null = null;
        if (storedQualification !== undefined) {
          // Use stored validation result (true -> 'valid', false -> 'invalid', null -> null)
          initialValidationStatus = storedQualification === true ? 'valid' : storedQualification === false ? 'invalid' : null;
        } else if (!hasManualAssignment && order.driver) {
          // Suggestion drivers are pre-validated as qualified
          initialValidationStatus = 'valid';
        }
        return (
          <DriverSelectorCell
            pronumber={order.pronumber}
            legnum={order.legnum || 0}
            driver={currentDriver}
            customerName={order.customerName}
            originAppointmentDate={order.originAppointmentDate}
            destinationAppointmentDate={order.destinationAppointmentDate}
            originCoordinates={order.originCoordinates}
            destinationCoordinates={order.destinationCoordinates}
            originName={order.originName}
            destinationName={order.destinationName}
            hazmat={order.hazmat}
            isDaycab={order.isDaycab}
            isTank={order.isTank}
            overweightPermitRequired={order.overweightPermitRequired}
            onDriverAssigned={handleDriverAssigned}
            onValidationChange={handleValidationChange}
            dispatchBlockedReason={blockingTroubleTicketMessage(order.openTickets, undefined, legAtOrBeforeRejectedGroup(order.dispatchScopeLegs, order.legnum))}
            dispatchWarnings={(() => {
              const warnings: string[] = [];
              const expired = redeliveryExpirationWarning(order, order.dispatchScopeLegs || order.legs || []);
              if (expired) warnings.push(expired);
              if (needsDropReleaseWarning({
                isDrop: order.isDrop,
                deliveryLeg: order.deliveryLeg,
                dropReleaseDatetime: order.dropReleaseDatetime,
                dispatchGroupLegnums: order.dispatchScopeLegs?.map(l => l.legnum) || order.legs?.map(l => l.legnum) || [],
              })) warnings.push(DROP_RELEASE_WARNING_MESSAGE);
              if (needsNotReceivingWarning({
                moveType: order.moveType,
                firstRcvDate: order.firstRcvDate,
                dispatchGroupLegs: order.legs || [],
              })) warnings.push(NOT_RECEIVING_WARNING_MESSAGE);
              if (needsNotRailbilledWarning({
                warnings: order.warnings,
                dispatchGroupLegs: order.legs || [],
              })) warnings.push(NOT_RAILBILLED_WARNING_MESSAGE);
              if (needsMissingPreadviseWarning({
                warnings: order.warnings,
                dispatchGroupLegs: order.legs || [],
              })) warnings.push(MISSING_PREADVISE_WARNING_MESSAGE);
              // Order-level, so no dispatch-group scoping — see openTroubleTicketCheck.
              const openTt = openTroubleTicketWarning(order.openTtCount);
              if (openTt) warnings.push(openTt);
              // Likewise order-level: the repair is on the equipment the whole
              // move rides — see openRepairOrderCheck.
              const openRo = openRepairOrderWarning(order.openRoCount);
              if (openRo) warnings.push(openRo);
              return warnings;
            })()}
            disabled={isConfirmed}
            allowMissingDates={true}
            initialValidationStatus={initialValidationStatus}
          />
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 180,
      cell: ({ row }) => {
        const order = row.original;
        if (order.isSubRow) return null;
        const orderKey = `${order.pronumber}-${order.legnum || 0}`;
        const isConfirmed = confirmedMoves.has(orderKey);
        const isPending = pendingConfirms.has(orderKey);
        const hasDriver = !!(driverAssignmentsRef.current[orderKey] || order.driver);
        const isQualified = driverQualificationsRef.current[orderKey];
        // Driver must exist and be qualified (true), or qualification hasn't been checked yet (null/undefined treated as OK for suggested drivers)
        const canConfirm = hasDriver && isQualified !== false;

        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleConfirmOrder(order)}
              disabled={isConfirmed || isPending || !canConfirm}
              className={`px-2 py-0.5 text-xs rounded transition-colors whitespace-nowrap ${
                isConfirmed
                  ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400'
                  : isPending
                  ? 'bg-yellow-500 text-white cursor-wait'
                  : canConfirm
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-gray-200 dark:bg-gray-600 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isConfirmed ? 'Confirmed' : isPending ? '...' : 'Confirm'}
            </button>
            <button
              onClick={() => handleSuggestAlternative(order)}
              disabled={isConfirmed}
              className="px-2 py-0.5 text-xs bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white rounded transition-colors whitespace-nowrap disabled:opacity-50"
            >
              {hasDriver ? 'Alt' : 'Suggest'}
            </button>
            <button
              onClick={() => handleShowOnMap(order)}
              className="p-1 rounded bg-blue-100 dark:bg-blue-900 hover:bg-blue-200 dark:hover:bg-blue-800 text-blue-600 dark:text-blue-400 transition-colors"
              title="Show Route"
            >
              <MapIcon className="h-4 w-4" />
            </button>
          </div>
        );
      },
    },
  ];
  // Note: driverAssignments and driverQualifications intentionally NOT in deps - accessed via refs
  // to prevent infinite re-render loops when assignment/validation changes recreate columns
  }, [confirmedMoves, pendingConfirms, getTopSuggestion, suggestionsByOrder, handleDriverAssigned, handleValidationChange, handleConfirmOrder, handleSuggestAlternative, handleShowOnMap, isViewed, markViewed]);

  // Attached columns blank on leg sub-rows, matching this board's own order-sourced
  // columns: an attached value is resolved per-ORDER, so showing it against one
  // particular leg would attribute the wrong leg's value.
  const { columns: smartDispatchColumns, availableFields } =
    useAdditionalFieldColumns<SmartDispatchRow>(fieldSelection, {
      baseColumns: baseSmartDispatchColumns,
      isSubRow: isSmartDispatchSubRow,
    });

  return (
    <div className={`${styles.pageContainer} ${showGridBorders ? styles.pageContainerBorders : ''} ${showGridHighlight ? styles.pageContainerHighlight : ''} ${showGridLabels ? styles.pageContainerLabels : ''}`}>
      {/* Config Display Bar */}
      {currentConfig && (
        <div className={`${styles.configBar} relative px-6 py-2 bg-gray-50 dark:bg-gray-850 border-b border-gray-200 dark:border-gray-700 text-sm`}>
          <div className="absolute top-2 right-6 flex items-center gap-3">
            <select
              value={configRegion}
              onChange={(e) => setConfigRegion(e.target.value)}
              className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-label"
            >
              <option value="">Select Region...</option>
              {regionOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {canEditConfig && configRegion && (
              <button
                onClick={() => setIsConfigModalOpen(true)}
                className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm font-medium"
              >
                <Cog6ToothIcon className="h-4 w-4" />
                Edit
              </button>
            )}
          </div>
          <div className="flex items-center gap-4">
            <span className="text-muted font-medium">Scoring:</span>
            <span className="text-label">
              Distance {(currentConfig.weight_distance * 100).toFixed(0)}%
            </span>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <span className="text-label">
              Workload {(currentConfig.weight_workload * 100).toFixed(0)}%
            </span>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <span className="text-label">
              Profitability {(currentConfig.weight_profitability * 100).toFixed(0)}%
            </span>
          </div>
          <div className="flex items-center gap-4 mt-1">
            <span className="text-muted font-medium">Limits:</span>
            <span className="text-label">
              Timeout {currentConfig.timeout_default_minutes}min
            </span>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <span className="text-label">
              Max {currentConfig.max_suggestions_per_move} suggestions
            </span>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <span className="text-label">
              Max {currentConfig.distance_max_miles} mi
            </span>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <span className="text-label">
              Min Score {(currentConfig.min_score_threshold * 100).toFixed(0)}%
            </span>
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <FilterBar label="Filters:" className={styles.filterBar}>
        <GridLabel areaName="filterBar" position="top-right" />
        <OrderDispatcherFilter
          value={selectedOrderDispatchers}
          onChange={setSelectedOrderDispatchers}
        />
        <DriverDispatcherFilter
          value={selectedDriverDispatchers}
          onChange={setSelectedDriverDispatchers}
        />
        <MultiSelect
          options={regionOptions}
          value={selectedRegions}
          onChange={setSelectedRegions}
          placeholder="All Regions"
          showClearOption={true}
          clearOptionLabel="All Regions"
          enableSearch={true}
          searchPlaceholder="Search regions..."
          size="sm"
          className="min-w-[130px]"
        />
        <MultiSelect
          options={customerOptions}
          value={selectedCustomers}
          onChange={handleCustomersChange}
          placeholder="All Customers"
          showClearOption={true}
          clearOptionLabel="All Customers"
          enableSearch={true}
          searchPlaceholder="Search customers..."
          size="sm"
          className="min-w-[140px]"
        />
      </FilterBar>

      {/* Body */}
      <div className={`${styles.body} p-6`} style={{ position: 'relative' }}>
        <GridLabel areaName="body" position="top-right" />
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <ArrowPathIcon className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : Object.keys(ordersByRegion).length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-muted">
            <SparklesIcon className="h-16 w-16 mb-4 text-gray-300 dark:text-gray-600" />
            <h3 className="text-lg font-medium mb-2">No Orders Pending Assignment</h3>
            <p className="text-sm">Waiting for new orders to be processed by the dispatch engine</p>
            <p className="text-xs mt-2 text-gray-400">Auto-refreshes every {BACKGROUND_REFRESH_INTERVAL / 1000} seconds</p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(ordersByRegion).map(([region, regionOrders]) => {
              const confirmedCount = regionOrders.filter(o => confirmedMoves.has(`${o.pronumber}-${o.legnum || 0}`)).length;

              return (
                <TanTable
                  key={region}
                  settingsKey="smart-dispatch"
                  cardTitle={`${region} (${regionOrders.length} orders${confirmedCount > 0 ? `, ${confirmedCount} confirmed` : ''})`}
                  data={transformedOrdersByRegion[region] || []}
                  columns={smartDispatchColumns}
                  availableFields={availableFields}
                  additionalFields={fieldSelection.additionalFields}
                  onAdditionalFieldsChange={fieldSelection.onAdditionalFieldsChange}
                  getSubRows={(row) => row.subLegs}
                  defaultExpanded={true}
                  superCompact={true}
                  enableColumnFilters={true}
                  enableSearch={true}
                  lockedColumns={[
                    { id: 'proLeg', side: 'left' },
                    { id: 'driver', side: 'right' },
                    { id: 'actions', side: 'right' },
                  ]}
                  getRowClassName={(order) => {
                    const orderKey = `${order.pronumber}-${order.legnum || 0}`;
                    const isConfirmed = confirmedMoves.has(orderKey);
                    if (order.isSubRow) return 'bg-gray-50/50 dark:bg-gray-800/50';
                    if (isConfirmed) return 'bg-green-50 dark:bg-green-900/20';
                    return '';
                  }}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Route Map Drawer */}
      <RouteMapModal
        open={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          driverDrawer.closeDrawer();
        }}
        mode="drawer"
        stops={routeStopsForMap}
        pronumber={selectedOrderForMap?.pronumber}
        legnum={selectedOrderForMap?.legnum}
        customerName={selectedOrderForMap?.customerName}
        drivers={driverDrawer.drivers}
        driversLoading={driverDrawer.loading}
        driversError={driverDrawer.error}
        onSelectDriver={handleMapDriverSelect}
      />

      {/* Alternative Suggestions Drawer */}
      <Drawer
        isOpen={isAlternativesDrawerOpen}
        onClose={() => setIsAlternativesDrawerOpen(false)}
        title={alternativesOrder ? `Alternative Drivers: PRO ${alternativesOrder.pronumber}` : 'Alternative Drivers'}
        position="right"
        maxWidth="lg"
        noBackdrop={true}
      >
        {alternativesOrder && (
          <div className="space-y-4">
            {/* Order Summary */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <h4 className="font-medium text-heading mb-2">Order Details</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted">PRO#:</span>
                  <span className="ml-2 text-heading">{alternativesOrder.pronumber}-{alternativesOrder.legnum || 0}</span>
                </div>
                <div>
                  <span className="text-muted">Customer:</span>
                  <span className="ml-2 text-heading">{alternativesOrder.customerName}</span>
                </div>
              </div>
            </div>

            {/* Summary */}
            {!loadingAlternatives && (
              <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3">
                <div className="text-sm text-blue-900 dark:text-blue-100">
                  <span className="font-medium">Checked {totalDriversChecked} drivers:</span>
                  {' '}{alternativeSuggestions.length} qualified, {disqualifiedDrivers.length} disqualified
                </div>
              </div>
            )}

            {/* Loading State */}
            {loadingAlternatives ? (
              <div className="flex items-center justify-center py-8">
                <ArrowPathIcon className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : alternativeSuggestions.length === 0 && disqualifiedDrivers.length === 0 ? (
              <div className="text-center py-8 text-muted">
                <SparklesIcon className="h-12 w-12 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                <p>No drivers available</p>
              </div>
            ) : (
              <>
                {alternativeSuggestions.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-medium text-heading">Suggested Drivers</h4>
                {alternativeSuggestions.map((alt, index) => (
                  <div
                    key={alt.driver}
                    className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-rule hover:border-purple-300 dark:hover:border-purple-600 transition-colors cursor-pointer"
                    onClick={() => {
                      handleDriverAssigned(alternativesOrder.pronumber, alternativesOrder.legnum || 0, alt.driver);
                      setIsAlternativesDrawerOpen(false);
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg font-semibold text-heading">
                            {alt.driver}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getScoreColor(alt.total_score)}`}>
                            {formatScore(alt.total_score)}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-muted">Distance:</span>
                            <span className="ml-1 text-heading">{alt.distance_miles?.toFixed(1)} mi</span>
                          </div>
                          <div>
                            <span className="text-muted">Dispatches:</span>
                            <span className="ml-1 text-heading">{alt.remaining_dispatches}</span>
                          </div>
                          <div>
                            <span className="text-muted">Today's Pay:</span>
                            <span className="ml-1 text-heading">${alt.current_pay?.toFixed(0)}</span>
                          </div>
                          <div>
                            <span className="text-muted">Daily Goal:</span>
                            <span className="ml-1 text-heading">${alt.daily_goal?.toFixed(0)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-rule">
                      <div className="flex gap-2 text-xs">
                        <span className="text-muted">Scores:</span>
                        <span className="text-label">
                          Dist: {formatScore(alt.score_distance)}
                        </span>
                        <span className="text-label">
                          Work: {formatScore(alt.score_workload)}
                        </span>
                        <span className="text-label">
                          Profit: {formatScore(alt.score_profitability)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
                )}

                {/* Disqualified Drivers Section */}
                {disqualifiedDrivers.length > 0 && (
                  <div className="space-y-3 mt-6">
                    <button
                      onClick={() => setShowDisqualified(!showDisqualified)}
                      className="flex items-center gap-2 w-full text-left"
                    >
                      <h4 className="font-medium text-label">
                        Why No Qualified Drivers? ({disqualifiedDrivers.length})
                      </h4>
                      <ChevronDownIcon className={`h-4 w-4 text-gray-500 transition-transform ${showDisqualified ? 'rotate-180' : ''}`} />
                    </button>

                    {showDisqualified && (
                      <div className="space-y-2">
                        {disqualifiedDrivers.map((disq) => (
                          <div
                            key={disq.driver}
                            className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-rule"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-semibold text-heading">
                                    {disq.driver}
                                  </span>
                                  <span className="text-xs text-muted">
                                    ({disq.distance_miles?.toFixed(1)} mi away)
                                  </span>
                                </div>
                                <div className="text-xs text-red-600 dark:text-red-400">
                                  {disq.missing_requirements && disq.missing_requirements.length > 0 ? (
                                    <ul className="list-disc list-inside space-y-0.5">
                                      {disq.missing_requirements.map((req: string, idx: number) => (
                                        <li key={idx}>{req}</li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <span>Not qualified</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Debug Info Section */}
                {debugInfo && (
                  <div className="space-y-3 mt-6 border-t border-rule pt-4">
                    <button
                      onClick={() => setShowDebug(!showDebug)}
                      className="flex items-center gap-2 w-full text-left"
                    >
                      <h4 className="font-medium text-muted text-sm">
                        Debug Info
                      </h4>
                      <ChevronDownIcon className={`h-4 w-4 text-gray-500 transition-transform ${showDebug ? 'rotate-180' : ''}`} />
                    </button>

                    {showDebug && (
                      <div className="bg-gray-900 dark:bg-gray-950 rounded-lg p-3 text-xs text-green-400 overflow-x-auto">
                        <div className="space-y-1">
                          <div><span className="text-gray-500">Move Type:</span> {debugInfo.movetype}</div>
                          <div><span className="text-gray-500">Leg:</span> {debugInfo.legnum} (Originate: {debugInfo.originateleg}, Delivery: {debugInfo.deliveryleg}, Terminate: {debugInfo.terminateleg})</div>
                          <div><span className="text-gray-500">Loaded Leg:</span> {debugInfo.loadedleg ? 'YES' : 'NO'}</div>
                          <div className="border-t border-gray-700 pt-1 mt-1">
                            <div className="text-gray-500">Requirements:</div>
                            <div className="pl-2">
                              <div>Hazmat: {debugInfo.hazmat || 'N'}</div>
                              <div>Tank: {debugInfo.istank || 'N'}</div>
                              <div>Daycab: {debugInfo.isdaycab || 'N'}</div>
                              <div>Equip Type: {debugInfo.equiptype || 'N/A'}</div>
                              <div>Overweight: {debugInfo.overweightpermitrequired || 'N'}</div>
                            </div>
                          </div>
                          <div className="border-t border-gray-700 pt-1 mt-1">
                            <div><span className="text-gray-500">Origin Coords:</span> {debugInfo.origin_coordinates || 'MISSING'}</div>
                            <div><span className="text-gray-500">Dest Coords:</span> {debugInfo.dest_coordinates || 'MISSING'}</div>
                          </div>
                          <div className="border-t border-gray-700 pt-1 mt-1">
                            <div><span className="text-gray-500">Total Drivers Available:</span> {totalDriversChecked}</div>
                            <div><span className="text-gray-500">Skipped (no coords):</span> {debugInfo.skipped_no_coords}</div>
                            <div><span className="text-gray-500">Scored:</span> {debugInfo.total_scored}</div>
                            <div><span className="text-gray-500">Qualified:</span> {debugInfo.qualified_count}</div>
                            <div><span className="text-gray-500">Disqualified:</span> {debugInfo.disqualified_count}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Drawer>

      {/* Config Edit Modal */}
      <SmartDispatchConfigModal
        isOpen={isConfigModalOpen}
        onClose={() => setIsConfigModalOpen(false)}
        selectedRegion={configRegion}
        mode="smart-dispatch"
        onSave={() => {
          loadCurrentConfig(configRegion || undefined);
          loadConfigStats();
        }}
      />

      {/* Order Details Drawer */}
      {selectedOrder && (
        <ProDetailsView
          isOpen={isOrderModalOpen}
          pronumber={selectedOrder.pronumber}
          legnum={selectedOrder.legnum}
          onClose={closeOrderModal}
        />
      )}
    </div>
  );
}

function SmartDispatch() {
  return (
    <ViewedProsProvider>
      <SmartDispatchContent />
    </ViewedProsProvider>
  );
}

export default SmartDispatch;
